<template>
  <div
    :id="`face-engraving-${activeFace.face_id}`"
    style="width:100%;height:100%;"
  ></div>
</template>
<script>
import { markRaw, toRaw } from "vue";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { DragControls } from "three/examples/jsm/controls/DragControls";
import { Text } from "troika-three-text";
import getPosInfo from "../../utils/getPosInfo";
import calcOffset from "../../utils/calcOffset";

import { getAngleNumber } from "../../utils/calcAngle";
import modelToMesh from "../../utils/modelToMesh";

export default {
  name: "ThreeSceneEngraving",
  data() {
    return {
      canvas: null,
      renderer: null,
      scene: null,
      camera: null,
      gltfLoader: null,
      directionalLight: null,
      sizes: null,
      textureLoader: null,
      faceElementsGroup: null,
      faceTextsGroup: null,
      outlineBorder: null,
      mainElement: null,
      controls: null,
      dragControls: null,
      transformControls: null,
      objects: [],
      selection: null,
      pointer: null,
      raycaster: null,
    };
  },
  methods: {
    getMaterialTexture: async function (
      path = null,
      model = false,
      land = false
    ) {
      let material = null;
      if (model) {
        material = new THREE.MeshPhysicalMaterial({
          clearcoat: 0.5,
          flatShading: true,
          roughness: 0.2,
          metalness: 0.7,
          depthTest: true,
          depthWrite: true,
          reflectivity: 0.9,
        });
      } else {
        material = new THREE.MeshStandardMaterial({
          side: THREE.DoubleSide,
        });
      }
      if (path) {
        // const texture = await this.textureLoader.load(path);
        const [texture] = await Promise.all([
          this.textureLoader.loadAsync(path),
        ]);
        if (land) {
          texture.repeat.set(8, 8);
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
        }
        material.map = texture;
      } else if (!model) {
        material.color = new THREE.Color("#aeaece");
      }
      return material;
    },
    getGLTF: async function (path) {
      const [gltf] = await Promise.all([this.gltfLoader.loadAsync(path)]);
      return gltf;
    },
    async addElement(el) {
      let gltf = await this.getGLTF(el.model3d);

      if (gltf) {
        if (
          parseInt(el.individual_size_material) == 0 &&
          el.material &&
          el.material.image
        ) {
          const materialImage = el.material.imageOriginal || el.material.image;
          const material = await this.getMaterialTexture(
            materialImage,
            true,
            true
          );
          gltf.scene.traverse(function (node) {
            if (node.isMesh) {
              if (!node.name.includes("_notexture")) {
                node.material = material;
              }
            }
          });
          if (el.element_group_slug == "cvetniki-nadgrobiya") {
            let cvetnikMaterial = new THREE.MeshStandardMaterial({
              color: 0xcecece,
            });
            if (parseInt(this.settings.filler_cvetnik_texture) != 1) {
              let cvetnikMaterialTexture = "/textures/cvetnik/grass.jpg";
              if (parseInt(this.settings.filler_cvetnik_texture) == 2) {
                cvetnikMaterialTexture =
                  this.settingImages.filler_cvetnik_texture;
              }
              cvetnikMaterial = await this.getMaterialTexture(
                cvetnikMaterialTexture,
                false,
                true
              );
            }
            gltf.scene.traverse(function (node) {
              if (node.isMesh) {
                if (node.name.includes("_grass")) {
                  node.material = cvetnikMaterial;
                }
              }
            });
          }
        }

        let scaleCoef = { x: 1, y: 1, z: 1 };
        let model = null;

        if (el.size) {
          if (el.size.model3d) {
            const sizeGltf = await this.getGLTF(el.size.model3d);
            if (sizeGltf) {
              if (
                parseInt(el.individual_size_material) == 0 &&
                el.material &&
                el.material.image
              ) {
                const material = await this.getMaterialTexture(
                  el.material.image,
                  true,
                  true
                );
                sizeGltf.scene.traverse(function (node) {
                  if (node.isMesh) {
                    if (!node.name.includes("_notexture")) {
                      node.material = material;
                    }
                  }
                });
              }
              model = await modelToMesh(sizeGltf.scene);
            }
          } else {
            if (
              el.size.width &&
              el.size.height &&
              el.size.length &&
              el.base_size &&
              el.base_size.width &&
              el.base_size.height &&
              el.base_size.length
            ) {
              scaleCoef = {
                x: parseFloat(el.size.width) / parseFloat(el.base_size.width),
                y: parseFloat(el.size.height) / parseFloat(el.base_size.height),
                z: parseFloat(el.size.length) / parseFloat(el.base_size.length),
              };

              model = await modelToMesh(gltf.scene);
              model.geometry.scale(scaleCoef.x, scaleCoef.y, scaleCoef.z);

              model.updateMatrix();
            }
          }
        }

        if (parseInt(this.activeFace.rotate_x)) {
          model.rotation["x"] = -getAngleNumber(
            parseInt(this.activeFace.rotate_x)
          );
        }

        if (parseInt(this.activeFace.rotate_y)) {
          model.rotation["y"] = -getAngleNumber(
            parseInt(this.activeFace.rotate_y)
          );
        }

        if (parseInt(this.activeFace.rotate_z)) {
          model.rotation["z"] = getAngleNumber(
            parseInt(this.activeFace.rotate_z)
          );
        }

        // three.js‑объекты помечаем как raw, чтобы Vue не оборачивал их в Proxy
        this.mainElement = markRaw(model);
        this.mainElement.isDraggable = false;
        this.scene.add(this.mainElement);

        const bboxMainElement = getPosInfo(this.mainElement);

        bboxMainElement.xSize = +bboxMainElement.xSize.toFixed(2);
        bboxMainElement.ySize = +bboxMainElement.ySize.toFixed(2);
        bboxMainElement.zSize = +bboxMainElement.zSize.toFixed(2);

        this.$store.dispatch(
          "ClientEngravingSettings/setMainElementInfo",
          bboxMainElement
        );

        this.fitCameraToObject();
      }
    },

    async getMesh() {
      let mesh = null;
      await this.mainElement.traverse(function (node) {
        if (node.isMesh) {
          mesh = node;
        }
      });
      return this.mainElement;
    },

    async fitCameraToObject() {
      const boundingBox = new THREE.Box3();

      const object = await this.getMesh();

      boundingBox.setFromObject(object);

      var middle = new THREE.Vector3();
      var size = new THREE.Vector3();
      boundingBox.getSize(size);
      const fov = this.camera.fov * (Math.PI / 180);
      const fovh = 2 * Math.atan(Math.tan(fov / 2) * this.camera.aspect);
      let dx = size.z / 2 + Math.abs(size.x / 2 / Math.tan(fovh / 2));
      let dy = size.z / 2 + Math.abs(size.y / 2 / Math.tan(fov / 2));
      let cameraZ = Math.max(dx, dy);

      this.camera.position.set(0, 0, cameraZ);

      const minZ = boundingBox.min.z;
      const cameraToFarEdge = minZ < 0 ? -minZ + cameraZ : cameraZ - minZ;

      this.camera.far = cameraToFarEdge * 3;
      this.camera.updateProjectionMatrix();
    },

    async addEngravingElement(el) {
      const elImage = el.imageOriginal || el.image;

      if (elImage) {
        var loader = new THREE.TextureLoader();
        const texture = loader.load(elImage);
        var material = new THREE.MeshToonMaterial({
          map: texture,
          alphaMap: texture,
          alphaTest: 0.01,
          transparent: true,
          //   depthWrite: false,
          //   depthTest: false,
          side: THREE.DoubleSide,
        });
        var geometry = new THREE.PlaneGeometry(el.width / 100, el.height / 100);
        var mesh = markRaw(new THREE.Mesh(geometry, material));

        if (el.mirrored) {
          mesh.rotation.y = Math.PI;
        }

        mesh.rotation.z = getAngleNumber(el.angle);

        this.$store.dispatch("clientOrder/updateEngravingMeshId", {
          element_uuid: el.uuid,
          meshId: mesh.uuid,
        });

        this.faceElementsGroup.add(mesh);

        // this.transformControls.attach(mesh);

        //высчитываем смещение для информации в панели
        if (this.mainElement) {
          const mainElementBbox = getPosInfo(this.mainElement);
          const meshBbox = getPosInfo(mesh);

          mesh.position.z = mainElementBbox.bbox.max.z + el.layer;

          const offsetTop = el.offsetTop / 100;
          const offsetLeft = el.offsetLeft / 100;

          mesh.position.y =
            mainElementBbox.bbox.max.y - meshBbox.ySize / 2 - offsetTop;

          mesh.position.x =
            mainElementBbox.bbox.min.x + meshBbox.xSize / 2 + offsetLeft;

          const offsetCenterY = (
            (mainElementBbox.bbox.max.y - meshBbox.bbox.max.y) *
            100
          ).toFixed(1);
          const offsetCenterX = (
            (mainElementBbox.bbox.max.x - meshBbox.bbox.max.x) *
            100
          ).toFixed(1);
          const offsetMaxY = (
            (mainElementBbox.ySize - meshBbox.ySize) *
            100
          ).toFixed(1);
          const offsetMaxX = (
            (mainElementBbox.xSize - meshBbox.xSize) *
            100
          ).toFixed(1);

          //посчитать максимальные и центральные смещения, заложить эти значения в элемент (для последующего выравнивания из панели), а здесь выяснять значения выравниваний

          el["offsetCenterY"] = offsetCenterY;
          el["offsetCenterX"] = offsetCenterX;
          el["offsetMaxY"] = offsetMaxY;
          el["offsetMaxX"] = offsetMaxX;

          el.verticalAlignment = "";
          el.horizontalAlignment = "";

          if (offsetTop == 0) {
            el.verticalAlignment = "top";
          }

          if (offsetTop * 100 == offsetCenterY) {
            el.verticalAlignment = "middle";
          }

          if (offsetTop * 100 == offsetMaxY) {
            el.verticalAlignment = "bottom";
          }

          if (offsetLeft == 0) {
            el.horizontalAlignment = "left";
          }

          if (offsetLeft * 100 == offsetCenterX) {
            el.horizontalAlignment = "center";
          }

          if (offsetLeft * 100 == offsetMaxX) {
            el.horizontalAlignment = "right";
          }
        }
        this.$store.dispatch("clientOrder/updateEngravingPosition", {
          uuid: mesh.uuid,
          position: {
            x: mesh.position.x,
            y: mesh.position.y,
            z: mesh.position.z,
          },
        });

        if (this.selection && this.selection.uuid == el.meshId) {
          mesh.material.color = new THREE.Color(0x00ff00);
          this.selection = mesh;
          this.$store.dispatch("ClientEngravingSetting/setItem", el);
        }
        mesh.element_uuid = el.uuid;
        this.objects.push(mesh);
        this.initDragControls();
      }
    },

    async addText(text) {
      const myText = markRaw(new Text());

      this.faceTextsGroup.add(myText);

      myText.text = text.text;
      myText.fontSize = parseFloat(text.size.height) / 100;
      myText.font = text.font.file;
      myText.lineHeight = 1;
      myText.outlineWidth = 0;

      if (text.align && text.align.id) {
        myText.textAlign = text.align.id;
      }

      myText.color = 0xffffff;

      myText.isText = true;
      myText.element_uuid = text.uuid;

      this.$store.dispatch("clientOrder/updateEngravingMeshId", {
        element_uuid: text.text_id,
        meshId: myText.uuid,
      });

      if (text.angle) {
        myText.rotation.z = getAngleNumber(text.angle);
      }

      //высчитываем смещение для информации в панели
      if (this.mainElement) {
        const mainElementBbox = getPosInfo(this.mainElement);
        myText.maxWidth = mainElementBbox.xSize;

        myText.position.z = mainElementBbox.bbox.max.z + text.layer;

        const offsetTop = text.offsetTop / 100;
        const offsetLeft = text.offsetLeft / 100;

        myText.position.x = mainElementBbox.bbox.min.x + offsetLeft;
        myText.position.y = mainElementBbox.bbox.max.y - offsetTop;

        const offsetCenterY = (mainElementBbox.bbox.max.y * 100).toFixed(1);
        const offsetCenterX = (mainElementBbox.bbox.max.x * 100).toFixed(1);
        const offsetMaxY = (mainElementBbox.ySize * 100).toFixed(1);
        const offsetMaxX = (mainElementBbox.xSize * 100).toFixed(1);

        //посчитать максимальные и центральные смещения, заложить эти значения в элемент (для последующего выравнивания из панели), а здесь выяснять значения выравниваний

        text["offsetCenterY"] = offsetCenterY;
        text["offsetCenterX"] = offsetCenterX;
        text["offsetMaxY"] = offsetMaxY;
        text["offsetMaxX"] = offsetMaxX;

        text.verticalAlignment = "";
        text.horizontalAlignment = "";

        if (offsetTop == 0) {
          text.verticalAlignment = "top";
        }

        if (offsetTop * 100 == offsetCenterY) {
          text.verticalAlignment = "middle";
        }

        if (offsetTop * 100 == offsetMaxY) {
          text.verticalAlignment = "bottom";
        }

        if (offsetLeft == 0) {
          text.horizontalAlignment = "left";
        }

        if (offsetLeft * 100 == offsetCenterX) {
          text.horizontalAlignment = "center";
        }

        if (offsetLeft * 100 == offsetMaxX) {
          text.horizontalAlignment = "right";
        }
      }
      this.$store.dispatch("clientOrder/updateEngravingPosition", {
        uuid: myText.uuid,
        position: {
          x: myText.position.x,
          y: myText.position.y,
          z: myText.position.z,
        },
      });
      if (this.selection && this.selection.uuid == text.meshId) {
        myText.color = 0x00ff00;
        this.selection = myText;
        this.$store.dispatch("ClientEngravingSetting/setItem", text);
      }

      myText.sync();
      this.objects.push(myText);
      this.initDragControls();
    },

    dragStart: async function (event) {
      event.object.material.opacity = 0.33;
      this.controls.enabled = false;

      if (this.selection && this.selection.uuid != event.object.uuid) {
        if (this.selection.isText) {
          this.selection.color = 0xffffff;
        } else {
          this.selection.material.color = new THREE.Color(0xffffff);
        }
        this.selection = null;
      }

      this.selection = event.object;

      if (this.selection.isText) {
        this.selection.color = 0x00ff00;
      } else {
        this.selection.material.color = new THREE.Color(0x00ff00);
      }

      const engElement = await this.$store.dispatch(
        "clientOrder/getEngravingElementByMeshId",
        event.object.uuid
      );

      if (engElement) {
        this.$store.dispatch("ClientEngravingSettings/setItem", {
          ...engElement,
          changeFromScene: true,
        });
      }
    },
    drag: async function (event) {
      this.render();

      let offset = calcOffset(
        getPosInfo(this.mainElement),
        getPosInfo(event.object)
      );
      offset = { ...offset, changeFromScene: true };
      this.$store.dispatch("ClientEngravingSettings/setOffset", offset);
    },
    dragEnd: async function (event) {
      event.object.material.opacity = 1;
      this.controls.enabled = true;

      let offset = calcOffset(
        getPosInfo(this.mainElement),
        getPosInfo(event.object)
      );
      this.$store.dispatch("clientOrder/updateEngravingElement", {
        element_uuid: this.itemInPanel.uuid,
        value: offset.offsetLeft.toFixed(1),
        key: "offsetLeft",
      });
      this.$store.dispatch("clientOrder/updateEngravingElement", {
        element_uuid: this.itemInPanel.uuid,
        value: offset.offsetTop.toFixed(1),
        key: "offsetTop",
      });
    },

    doubleClick: function () {
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      } else {
        document.selection.empty();
      }
      if (this.selection) {
        this.$store.dispatch("ClientEngravingSettings/setShow", true);
      }
      return false;
    },

    clearSelection: function () {
      if (this.selection) {
        if (this.selection.isText) {
          this.selection.color = 0xffffff;
        } else {
          this.selection.material.color = new THREE.Color(0xffffff);
        }
        this.selection = null;
        this.$store.dispatch("ClientEngravingSettings/clear");
      }
    },

    isContainInObjects: function (uuid) {
      return this.objects.find((obj) => obj.uuid == uuid);
    },

    onClickHandler: function (event) {
      if (this.selection) {
        this.pointer.set(
          (event.clientX / window.innerWidth) * 2 - 1,
          -(event.clientY / window.innerHeight) * 2 + 1
        );
        this.raycaster.setFromCamera(this.pointer, this.camera);

        const intersects = this.raycaster.intersectObjects(
          this.scene.children,
          true
        );

        if (intersects.length > 0) {
          const intersect = intersects[0];
          if (
            intersect.object &&
            !this.isContainInObjects(intersect.object.uuid)
          ) {
            this.clearSelection();
          }
        } else {
          this.clearSelection();
        }
      }
    },

    initDragControls: async function () {
      const draggable = this.objects.map((obj) => toRaw(obj));
      this.dragControls = markRaw(
        new DragControls([...draggable], this.camera, this.renderer.domElement)
      );

      this.dragControls.addEventListener("drag", this.drag);
      this.dragControls.addEventListener("dragstart", this.dragStart);
      this.dragControls.addEventListener("dragend", this.dragEnd);

      //   this.dragControls.addEventListener("hoveron", function (event) {
      //     console.log("hoveron", event);
      //   });

      //   this.dragControls.addEventListener("hoveroff", function (event) {
      //     console.log("hoveroff", event);
      //   });
    },

    init: async function () {
      this.canvas = document.getElementById(
        `face-engraving-${this.activeFace.face_id}`
      );
      this.gltfLoader = markRaw(new GLTFLoader());
      this.textureLoader = markRaw(new THREE.TextureLoader());
      this.faceElementsGroup = markRaw(new THREE.Group());
      this.faceTextsGroup = markRaw(new THREE.Group());
      this.scene = markRaw(new THREE.Scene());
      this.scene.add(this.faceElementsGroup, this.faceTextsGroup);
      this.scene.background = null;
      // чуть более мягкий свет, чтобы убрать пересвеченность
      this.directionalLight = markRaw(
        new THREE.DirectionalLight("#ffffff", 2.2)
      );
      this.directionalLight.position.set(0, 2, 1);
      this.scene.add(this.directionalLight);
      this.sizes = {
        width: this.canvas?.clientWidth,
        height: this.canvas?.clientHeight,
      };
      this.camera = markRaw(
        new THREE.PerspectiveCamera(
        50,
        (this.sizes.width * 2) / (this.sizes.height * 2),
        0.01,
        30000
      )
      );

      this.camera.position.set(0, 0, 1.9);
      this.camera.lookAt(new THREE.Vector3(0, 0, 0));

      this.scene.add(this.camera);

      this.controls = new OrbitControls(this.camera, this.canvas);
      this.controls.enableDamping = true;
      this.controls.enableRotate = false;

      this.renderer = markRaw(
        new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      })
      );

      this.renderer.setSize(this.sizes.width, this.sizes.height);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.canvas.appendChild(this.renderer.domElement);
      this.raycaster = markRaw(new THREE.Raycaster());
      this.pointer = markRaw(new THREE.Vector2());

      // const axesHelper = new THREE.AxesHelper();
      // axesHelper.setColors(
      //   new THREE.Color(0xff0000),
      //   new THREE.Color(0xffff00),
      //   new THREE.Color(0x000000)
      // );
      // this.scene.add(axesHelper);

      this.canvas.addEventListener("dblclick", this.doubleClick);
      //   this.canvas.addEventListener("click", this.onClickHandler);
    },

    animate: function () {
      this.controls.update();
      requestAnimationFrame(this.animate);
      this.render();
    },
    render: function () {
      this.renderer.render(this.scene, this.camera);
    },
    nodeChangeOffset(item, targetNode) {
      const mainElementBbox = getPosInfo(this.mainElement);
      const meshBbox = getPosInfo(targetNode);
      const offsetTop = item.offsetTop / 100;
      targetNode.position.y =
        mainElementBbox.bbox.max.y - meshBbox.ySize / 2 - offsetTop;
      const offsetLeft = item.offsetLeft / 100;
      targetNode.position.x =
        mainElementBbox.bbox.min.x + meshBbox.xSize / 2 + offsetLeft;
      targetNode.updateMorphTargets();
      //изменить offsetCenter
      this.itemChangeCenters(targetNode);
    },
    itemChangeCenters(targetNode) {
      const mainElementBbox = getPosInfo(this.mainElement);
      const meshBbox = getPosInfo(targetNode);

      const offsetCenterY = (
        (mainElementBbox.bbox.max.y - meshBbox.bbox.max.y) *
        100
      ).toFixed(1);

      this.$store.dispatch("ClientEngravingSettings/updateItem", {
        value: offsetCenterY,
        key: "offsetCenterY",
        changeFromScene: true,
      });

      const offsetCenterX = (
        (mainElementBbox.bbox.max.x - meshBbox.bbox.max.x) *
        100
      ).toFixed(1);
      this.$store.dispatch("ClientEngravingSettings/updateItem", {
        value: offsetCenterX,
        key: "offsetCenterX",
        changeFromScene: true,
      });

      const offsetMaxY = (
        (mainElementBbox.ySize - meshBbox.ySize) *
        100
      ).toFixed(1);
      this.$store.dispatch("ClientEngravingSettings/updateItem", {
        value: offsetMaxY,
        key: "offsetMaxY",
        changeFromScene: true,
      });

      const offsetMaxX = (
        (mainElementBbox.xSize - meshBbox.xSize) *
        100
      ).toFixed(1);
      this.$store.dispatch("ClientEngravingSettings/updateItem", {
        value: offsetMaxX,
        key: "offsetMaxX",
        changeFromScene: true,
      });
    },
  },
  async mounted() {
    await this.init();
    this.animate();

    if (this.activeFace.element.model3d) {
      await this.addElement(this.activeFace.element);
    }
    if (this.activeFace.elements.length) {
      for (const el of this.activeFace.elements) {
        await this.addEngravingElement(el);
      }
    }
    if (this.activeFace.texts.length) {
      for (const text of this.activeFace.texts) {
        await this.addText(text);
      }
    }
  },
  computed: {
    settings() {
      return this.$store.state.settings.settings;
    },
    settingImages() {
      return this.$store.state.settings.settingImages;
    },
    activeFace() {
      return this.$store.getters["clientOrder/activeFace"];
    },
    faces() {
      return this.$store.state.clientOrder.faces;
    },

    itemInPanel() {
      return this.$store.state.ClientEngravingSettings.item;
    },
  },
  watch: {
    async faces(fcs) {
      this.faceElementsGroup.clear();
      this.faceTextsGroup.clear();
      this.objects = [];

      fcs.forEach(async (fc) => {
        if (
          (!this.isConfirm &&
            this.activeFace &&
            fc.face_id == this.activeFace.face_id) ||
          (this.isConfirm && fc.face_id == this.face.face_id)
        ) {
          if (fc.elements.length) {
            for (const el of fc.elements) {
              await this.addEngravingElement(el);
            }
          }
          if (fc.texts.length) {
            for (const text of fc.texts) {
              await this.addText(text);
            }
          }
        }
      });

      //   await this.initDragControls();
      //   console.log(this.dragControls.getObjects());
    },

    async itemInPanel(item) {
      // Может приходить null/undefined при очистке выбранного элемента в панели.
      // В этом случае нет meshId, и изменять сцену не требуется.
      if (!item || !item.meshId) {
        return;
      }

      let targetNode = null;

      this.scene.traverse((node) => {
        if (node.isMesh || node.isText) {
          if (node.uuid == item.meshId) {
            targetNode = node;
          }
        }
      });

      if (targetNode) {
        if (!item.changeFromScene) {
          const mainElementBbox = getPosInfo(this.mainElement);

          let meshBbox = {
            min: {
              x: 0,
              y: 0,
              z: 0,
            },
            max: {
              x: 0,
              y: 0,
              z: 0,
            },
            xSize: 0,
            ySize: 0,
            zSize: 0,
          };

          if (!targetNode.isText) {
            meshBbox = getPosInfo(targetNode);
          }

          {
            //изменение смещения
            if (item.action == "offsetTop") {
              const offsetTop = item.offsetTop / 100;
              targetNode.position.y =
                mainElementBbox.bbox.max.y - meshBbox.ySize / 2 - offsetTop;
            }

            if (item.action == "offsetLeft") {
              const offsetLeft = item.offsetLeft / 100;
              targetNode.position.x =
                mainElementBbox.bbox.min.x + meshBbox.xSize / 2 + offsetLeft;
            }
          }

          {
            //изменение угла
            if (item.action == "angle") {
              targetNode.rotation.z = getAngleNumber(item.angle);
            }
          }

          {
            //изменение порядка слоёв
            if (item.action == "layer") {
              targetNode.position.z = mainElementBbox.bbox.max.z + item.layer;
            }
          }

          {
            //отразить
            if (item.action == "mirrored") {
              if (item.mirrored) {
                targetNode.rotation.y = Math.PI;
              } else {
                targetNode.rotation.y = 0;
              }
            }
          }

          {
            //изменение размеров
            if (
              item.action == "width" ||
              item.action == "height" ||
              item.action == "size"
            ) {
              var newGeometry = new THREE.PlaneGeometry(
                item.width / 100,
                item.height / 100
              );
              targetNode.geometry.dispose();
              targetNode.geometry = newGeometry;
              this.nodeChangeOffset(item, targetNode);
            }
          }
        }
      }

      //   this.faceElementsGroup.remove(targetNode);
      //   await this.addEngravingElement(item);
    },
  },
};
</script>
